/**
 * WAF bulk operations — apply one setting across many domains at once. Every
 * endpoint is `POST /services/:service_id/waf/bulk/<op>` with a body of
 * `{ domains: string[], ...fields }` and returns `{ applied: number }`.
 *
 * Backed by `modules/service/waf/controller_bulk.go`. One generic hook drives
 * every section of the Bulk Configure page; pass the op path + the typed body.
 */
import { useMutation, useQuery, type UseMutationOptions } from '@tanstack/react-query';
import { serviceApiV3, unwrap } from './_shared';

/** One entry of the WAF region catalog (for the bulk region selector). */
export interface WafRegion {
  code: string;
  display_name: string;
  type: string;
}

/** The Mlytics region catalog (`GET /waf/regions`). */
export function useWafRegions(serviceId: string | undefined, socMode = false) {
  return useQuery<WafRegion[]>({
    queryKey: ['waf', serviceId, 'regions', socMode],
    enabled: !!serviceId,
    staleTime: 30 * 60_000,
    queryFn: async () =>
      unwrap<WafRegion[]>(
        await serviceApiV3.get(`/services/${serviceId}/waf/regions`, { params: { soc_mode: socMode } }),
      ) ?? [],
  });
}

/** Bulk op result — how many domains the change was applied to. */
export interface WafBulkResult {
  applied: number;
}

/** The set of domains every bulk body carries. */
export interface WafBulkBase {
  domains: string[];
}

/** Bulk op path → request body shape. */
export interface WafBulkBodies {
  origin: WafBulkBase & { type: string; sources: string[] };
  'origin-protocol': WafBulkBase & { ssl_policies: string; port_http?: number; port_https?: number; sni?: boolean };
  'origin-ip': WafBulkBase & { sources: string[] };
  'origin-sources': WafBulkBase & { copy_from: string };
  region: WafBulkBase & { region: string };
  'auto-compression': WafBulkBase & { enable: boolean };
  'ignore-query-string': WafBulkBase & { enable: boolean };
  'ssl-redirect': WafBulkBase & { enable: boolean };
  'tiered-cache': WafBulkBase & { enable: boolean };
  'cache-rules': WafBulkBase & { scope: string; path: string; ttl: number; honor_origin: boolean };
  'cache-rules/copy': WafBulkBase & { copy_from: string; bucket: string };
  purge: WafBulkBase & { paths: string[] };
  'access-control': WafBulkBase & { list: string; action: string; values?: string[]; copy_from?: string };
  'cname-flatten': WafBulkBase & { flatten: boolean };
  'clone-optimization': { source: string; domains: string[] };
}

export type WafBulkOp = keyof WafBulkBodies;

/** Generic bulk mutation for one op path (`POST /waf/bulk/<op>`). */
export function useWafBulk<Op extends WafBulkOp>(
  serviceId: string | undefined,
  op: Op,
  options?: UseMutationOptions<WafBulkResult, Error, WafBulkBodies[Op]>,
) {
  return useMutation<WafBulkResult, Error, WafBulkBodies[Op]>({
    mutationFn: async (body) =>
      unwrap<WafBulkResult>(
        await serviceApiV3.post(`/services/${serviceId}/waf/bulk/${op}`, body),
      ),
    ...options,
  });
}
